/**
 * A Moon Exchange listing, arrived at.
 *
 * THE CLAIM TICKET. A card has been set aside and the link is the stub: the
 * stub holds the real card, the copy beside it says what it is, and one button
 * carries the action.
 *
 * ONE SCREEN, TWO DOORS. It is reached by clicking a row on the exchange and by
 * following a shared link, and those two must not be able to disagree -- the
 * first version could, and its own owner was offered a Buy button for their own
 * card because the public projection deliberately withholds the seller. So the
 * view takes ONE input shape and ONE `viewer` fact-set, and every difference
 * between the two doors lives in how that fact-set is obtained, never in what
 * is drawn from it.
 *
 * The viewer resolves in three states and the difference matters: a signed-in
 * player, a VISITOR with no account, and -- for a moment, on the link door
 * only -- NOT YET KNOWN. The third one is why the action area can be a
 * placeholder: showing "create an account" to someone who turns out to be
 * signed in, and then swapping it, is a visible lie for a few hundred
 * milliseconds. A placeholder is not.
 *
 * The seller is absent throughout, for the same reason they are absent from the
 * share card and the Open Graph tags: a forwarded link reaches people the
 * exchange screen never would.
 */

import { i18n } from '../i18n.js';
import { Cards } from './cards.js';
import { CardTilt } from './card-tilt.js';
import { RewardScene } from './reward-scene.js';
import { StellarAssets } from './stellar-assets.js';
import { openListingShare } from './exchange-share.js';
import { attachCardHoverPopup } from './card-hover-popup.js';
import { playerFacingError, exchangeLockMessage } from './player-error.js';

function uiText(key, fallback, vars) {
  try {
    const value = i18n?.t?.(key, vars);
    if (typeof value === 'string' && value && value !== key) return value;
  } catch (_) { /* copy must never break the view */ }
  const text = typeof fallback === 'string' ? fallback : key;
  if (!vars) return text;
  /* The fallback needs interpolating too — i18n resolves late, and returning
     the default verbatim printed a literal "{{price}} Stardust" on screen. */
  return text.replace(/\{\{(\w+)\}\}/g, (whole, name) => (
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : whole
  ));
}

/**
 * ONE SHAPE, WHATEVER THE DOOR.
 *
 * A listing arrives two ways carrying different objects: the exchange row's
 * `entry` (`cardImage`, `cardDescription`, no cost, no type) and the server's
 * public projection (`artUrl`, `description`, `manaCost`, `isSpell`). The view
 * read the projection's names, so a CLICKED listing silently lost its rules
 * text and rendered a costless card in the wrong frame.
 *
 * @param {object} raw either entry shape
 * @param {object} [card] the card library row, when the caller has one
 */
export function normalizeListing(raw, card = null) {
  if (!raw) return null;
  const pick = (...vals) => vals.find((v) => v !== undefined && v !== null && v !== '');
  const type = pick(raw.type, card?.type);
  return {
    id: String(raw.id || raw.listingId || ''),
    cardId: pick(raw.cardId, card?.id) || '',
    /* THE CARD'S OWN WORDS FIRST (28 September 2026). A listing's
       `cardName`/`cardDescription` are its snapshot, stored in English when
       the card was listed, so a ja/ko/… reader saw English here beside the
       localized faces of the exchange rows. The card in the reader's language
       — the row's catalogue or page card, the link's details read — names the
       listing; the snapshot is the last resort, for a card neither can read. */
    cardName: pick(card?.name, raw.cardBaseName, raw.cardName, raw.name) || '',
    description: pick(card?.description, raw.description, raw.cardDescription) || '',
    manaCost: Number(pick(raw.manaCost, card?.manaCost, card?.cost, 0)) || 0,
    isSpell: type ? type === 'Spell' : Boolean(raw.isSpell),
    isStellar: raw.isStellar === true || raw.variant === 'stellar',
    stellarVideoUrl: pick(raw.stellarVideoUrl) || null,
    stellarVideoWebmUrl: pick(raw.stellarVideoWebmUrl) || null,
    artUrl: pick(raw.artUrl, raw.cardImage, card?.image, card?.picture) || null,
    unitPrice: Number(raw.unitPrice) || 0,
    quantityRemaining: Number(raw.quantityRemaining) || 0,
    /* `available` is the server's word for "still an offer", and it is a
       BOOLEAN — it used to be read as a fallback stock count, which would have
       made a live listing report one print left. The exchange row only ever
       carries open listings, so its absence means available. */
    isAvailable: raw.available === undefined ? true : raw.available === true,
  };
}

/**
 * The viewer, normalized. `state` is the only thing that decides which action
 * is drawn, and `'resolving'` is a real state rather than a stand-in for
 * "guest" — see the header.
 */
function normalizeViewer(raw) {
  const state = raw?.state === 'player' || raw?.state === 'guest' ? raw.state : 'resolving';
  const balance = Number(raw?.stardustBalance);
  return {
    state,
    isMine: raw?.isMine === true,
    /* False only when the viewer's own facts could not be read at all. Not the
       same as "not yours": an unanswered question offers no action rather than
       guessing one. */
    actionable: raw?.actionable !== false,
    stardustBalance: Number.isFinite(balance) ? balance : undefined,
    /* The Moon Exchange unlock, when the caller knows it. The gate binds BUYING
       as well as selling and refuses server-side, so without this the panel
       offered a live Buy to a locked reader and answered the press with a stack
       frame. Absent means "not known", which offers the action as before. */
    exchangeUnlock: raw?.exchangeUnlock && raw.exchangeUnlock.unlocked !== true
      ? {
        unlocked: false,
        finished: Number(raw.exchangeUnlock.finished) || 0,
        required: Number(raw.exchangeUnlock.required) || 0,
      }
      : null,
  };
}

/**
 * The listing's card, built by the SHARED renderer.
 *
 * `docs/card-rendering-regression-contract.md` requires this to be the same
 * physical object as the card in a hand, a collection or a deck builder, so it
 * is never markup written for this screen.
 *
 * The DATA comes back with the element because the hover popup rebuilds the
 * card from it. Handing the popup the rendered node instead would clone a face
 * that has already been rastered and fitted to the ticket's small box.
 *
 * @returns {{ data: object, element: HTMLElement }|null}
 */
function buildCardFace(listing) {
  if (typeof Cards?.createCardElement !== 'function') return null;
  try {
    const data = {
      id: listing.cardId || listing.id,
      instanceId: `exchange-listing-${listing.id}`,
      name: listing.cardName || '',
      type: listing.isSpell ? 'Spell' : 'Character',
      manaCost: Number(listing.manaCost) || 0,
      picture: listing.cardId,
      imageUrl: listing.artUrl || undefined,
      description: listing.description || '',
      targetingType: 'None',
      isStellar: listing.isStellar === true,
    };

    /* A stellar listing is not the same card with a different border: it has its
       own media, applied through the same call the exchange rows use. Without
       it a stellar card sits in a stellar frame promising motion it never
       delivers. */
    if (listing.isStellar) {
      /* Prefer what the projection carried — it is the only source a signed-out
         reader has. Fall back to the client registry for the exchange row path,
         which already has it loaded. */
      const fromLink = listing.stellarVideoUrl
        ? {
          status: 'complete',
          videoUrl: listing.stellarVideoUrl,
          videoWebmUrl: listing.stellarVideoWebmUrl || undefined,
        }
        : null;
      const asset = fromLink || StellarAssets?.get?.(listing.cardId);
      if (asset?.status === 'complete') StellarAssets?.applyMediaToCard?.(data, asset);
    }

    const element = Cards.createCardElement(data, `exchange-listing-${listing.id}`);
    return element ? { data, element } : null;
  } catch (_) {
    return null;
  }
}

/**
 * The purchase label — ONE definition.
 *
 * The resolving placeholder is sized by this string and the real button carries
 * it, so the two must be byte-identical. Written out twice, they drifted by a
 * glyph (a hyphen against an em dash) within a day.
 */
function buyLabel(price) {
  return uiText('exchange.detail.claim', 'Buy — {{price}} Stardust', { price });
}

function row(parent, className, text) {
  const el = document.createElement('div');
  el.className = className;
  el.textContent = text;
  parent.appendChild(el);
  return el;
}

/**
 * The claim, celebrated on the object it happened to.
 *
 * A SPARK BURST WAS TRIED AND REMOVED. It rendered — 14 sparks, opacity 1, at
 * z-index 20 over the card — and was invisible anyway: gold 6px dots fired from
 * the centre of a card whose own art is bright gold. Frames proved it. What
 * reads instead is the TICKET: it pops, straightens out of its resting tilt and
 * takes a warm rim, which is legible against every card in the game.
 *
 * Presentation only, and silent under reduced motion.
 */
function celebrate(host) {
  if (!host) return;
  try {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return;
  } catch (_) { /* an engine without matchMedia still gets the beat */ }
  host.classList.add('exchange-ticket-claimed');
}

/** Is the Moon Exchange itself already the screen behind this dialog? */
/**
 * IS THE EXCHANGE ACTUALLY BEHIND THIS DIALOG?
 *
 * PRESENT IS NOT THE SAME AS ON SCREEN, and reading `getElementById` as if it
 * were deleted the only action a sold-out listing has. The collection modal
 * builds its markup once and keeps it in the DOM at `display: none`, so from
 * the first time a reader opened their collection the grid existed forever and
 * every later "Browse the Moon Exchange" was suppressed as redundant -- on a
 * screen where it was the one thing to press.
 */
function exchangeIsOnScreen() {
  let node = document.getElementById('moon-exchange-listings');
  if (!node) return false;
  for (; node && node !== document.body; node = node.parentElement) {
    if (getComputedStyle(node).display === 'none') return false;
  }
  return true;
}

let open = null;

/**
 * @param {'user'|'signin'|'browse'|'replaced'} [reason] why it closed. `'user'`
 *   is the reader dismissing it, and only that should stop the link door
 *   re-opening it after a sign-in the reader asked for.
 */
export function closeListingDetail(reason = 'user') {
  if (!open) return;
  const { onClose } = open;
  try { document.removeEventListener('keydown', open.onKey, true); } catch (_) { /* noop */ }
  /* The hover popup lives on `document.body`, not inside this panel, so
     removing the panel would leave an enlarged card floating over whatever the
     reader lands on next. */
  try { open.releaseHover?.(); } catch (_) { /* noop */ }
  try { open.el.parentNode.removeChild(open.el); } catch (_) { /* noop */ }
  open = null;
  try { onClose?.(reason); } catch (_) { /* a close handler must never trap the reader */ }
}

/** The listing currently on screen, or null. Read by the link door. */
export function currentListingId() {
  return open?.state?.listing?.id || null;
}

/**
 * Re-draw the action area of the OPEN panel.
 *
 * Not a re-open: re-creating the panel would re-mount the card, which re-runs
 * its raster and tilt and flashes. Identity arriving late must move exactly one
 * region of the screen.
 *
 * @param {{ viewer?: object, listing?: object }} [patch]
 * @returns {boolean} false when there is no open panel to update
 */
/**
 * Amend the panel THIS CALLER opened.
 *
 * `door` is not decoration. The link door's ownership answer can arrive after
 * the reader has closed that panel and opened another from an exchange row, and
 * without the check it overwrote the row's panel with the link's own viewer --
 * a screen the reader was looking at, changing under them, with facts they did
 * not ask for. A caller may only amend its own panel.
 */
/**
 * @param {object} [patch]
 * @param {object} [patch.viewer]
 * @param {object} [patch.listing]
 * @param {'link'|'row'} [patch.door] only this door's own panel is amended
 * @returns {boolean} whether a panel was amended
 */
export function updateListingDetail({ viewer, listing, door } = {}) {
  if (!open) return false;
  if (door && open.door && door !== open.door) return false;
  // The card the panel opened with keeps naming it: a correction never swaps its words.
  if (listing) open.state.listing = { ...open.state.listing, ...normalizeListing(listing, open.card) };
  if (viewer) open.state.viewer = normalizeViewer(viewer);
  open.render();
  return true;
}

/**
 * @param {object} rawListing
 * @param {object} [options]
 * @param {object} [options.card] the card in the reader's language (the
 *   row's catalogue or page card, the link's details read): its words name
 *   the listing, and it gives cost and type
 * @param {object} [options.viewer] `{ state, isMine, stardustBalance }`
 * @param {(listing: object, quantity: number) => Promise<any>} [options.buy]
 * @param {(listing: object) => Promise<any>} [options.cancel]
 * @param {(listing: object) => void} [options.onSignUp]
 * @param {(listing: object) => void} [options.onBrowse]
 * @param {(reason: string) => void} [options.onClose]
 * @param {'link'|'row'} [options.door] which door opened it
 */
export function openListingDetail(rawListing, options = {}) {
  closeListingDetail('replaced');
  const listing = normalizeListing(rawListing, options.card);
  if (!listing?.id) return null;

  const {
    buy, cancel, onSignUp, onBrowse, onClose,
    /* WHICH DOOR opened this — see `updateListingDetail`. */
    door,
  } = options;

  const el = document.createElement('div');
  el.className = 'starter-reward-overlay exchange-listing-overlay exchange-detail';
  el.id = 'exchange-detail';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');
  el.setAttribute('aria-labelledby', 'exchange-detail-name');
  /*
   * PUTTING THE ENLARGED CARD DOWN IS NOT CLOSING THE SCREEN.
   *
   * The backdrop closes this dialog, and the inspection popup covers most of
   * it — so the press that dismisses an enlargement lands on the backdrop far
   * more often than not. On touch it always does: the tap-away that releases
   * the inspection deliberately lets a click through to ordinary chrome, and
   * the reader who wanted to put the card down lost the listing they followed
   * a link to reach. The popup is gone by the time the click arrives, so the
   * question has to be asked at the PRESS.
   */
  let inspectingAtPress = false;
  el.addEventListener('pointerdown', () => {
    // A card being left on a catalog glide is still up but has given the id
    // up (hover-handoff.js, "ONE ELEMENT NAMES THE PREVIEW").
    inspectingAtPress = Boolean(document.querySelector(
      '#card-hover-preview, .card-hover-preview[data-hover-handoff]',
    ));
  }, true);
  el.addEventListener('click', (event) => {
    if (event.target !== el) return;
    if (inspectingAtPress) {
      inspectingAtPress = false;
      return;
    }
    closeListingDetail('user');
  });
  if (RewardScene?.createSparks) el.appendChild(RewardScene.createSparks());

  const panel = document.createElement('section');
  panel.className = 'starter-reward-card exchange-listing-card exchange-detail-card';

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'exchange-detail-close';
  close.setAttribute('aria-label', uiText('manga.nav.close', 'Close'));
  close.textContent = '×';
  close.addEventListener('click', () => closeListingDetail('user'));
  panel.appendChild(close);

  const stub = document.createElement('div');
  stub.className = 'exchange-ticket-stub';

  const stage = document.createElement('div');
  stage.className = 'exchange-detail-art exchange-listing-stage';
  const face = buildCardFace(listing);
  let releaseHover = () => {};
  if (face) {
    stage.appendChild(face.element);
    /* Presentation only, never gated on raster readiness. */
    try { CardTilt?.attach?.(stage, face.element); } catch (_) { /* noop */ }
    /* The card the reader came to look at answers a hover the way every other
       card in the game does: upright and enlarged, in the shared popup. The
       ticket keeps its resting tilt — only the inspection is square. */
    releaseHover = attachCardHoverPopup(face.element, {
      card: face.data,
      /* This dialog is the scroller on a short page box, so a wheel over the
         popup has to move it and not nothing. */
      scrollHost: () => el,
    });
  }
  stub.appendChild(stage);

  row(stub, 'exchange-ticket-serial', uiText(
    'exchange.detail.stub', 'ONE CARD · NO. {{serial}}',
    { serial: String(listing.id).slice(-6).toUpperCase() },
  ));
  panel.appendChild(stub);

  const body = document.createElement('div');
  body.className = 'exchange-detail-body';
  panel.appendChild(body);

  const eyebrow = document.createElement('p');
  eyebrow.className = 'starter-reward-eyebrow exchange-listing-eyebrow';
  body.appendChild(eyebrow);

  const name = document.createElement('h2');
  name.id = 'exchange-detail-name';
  name.className = 'starter-reward-title exchange-detail-name';
  name.textContent = String(listing.cardName || '');
  body.appendChild(name);

  if (listing.description) row(body, 'exchange-detail-desc', String(listing.description));

  const actions = document.createElement('div');
  actions.className = 'exchange-detail-actions';
  body.appendChild(actions);

  const facts = row(body, 'exchange-detail-facts', '');

  /* One live region for everything the panel has to say back — "completing
     purchase", an error, the confirmation. It lives HERE and not in the
     exchange grid: pressing Buy from this dialog used to re-press the row
     behind it, whose status message rendered underneath this overlay where
     nobody could read it. */
  const status = document.createElement('p');
  status.className = 'exchange-detail-status';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  status.hidden = true;
  body.appendChild(status);

  const state = {
    listing,
    viewer: normalizeViewer(options.viewer),
    /* `idle` | `busy` | `done`. A completed action replaces the call to action
       rather than leaving a button that would now do the wrong thing. */
    phase: 'idle',
    doneKind: 'buy',
    doneMessage: '',
  };

  function say(message, tone) {
    status.hidden = !message;
    status.textContent = message || '';
    status.dataset.tone = tone || '';
  }

  async function run(kind, action) {
    if (state.phase === 'busy') return;
    state.phase = 'busy';
    render();
    say(kind === 'cancel'
      ? uiText('collection.exchange.cancelingListing', 'Canceling listing...')
      : uiText('collection.exchange.completingPurchase', 'Completing purchase...'));
    try {
      await action();
      state.phase = 'done';
      state.doneKind = kind;
      /* The one beat on this screen worth celebrating — a card that cost real
         Stardust deserves more than a line of bold text. */
      if (kind !== 'cancel') celebrate(stub);
      state.doneMessage = kind === 'cancel'
        ? uiText('collection.exchange.canceledListing', 'Listing canceled. Prints returned.')
        : uiText('exchange.detail.bought', 'It is yours. Find it in your collection.');
      say('');
    } catch (error) {
      state.phase = 'idle';
      /* NEVER `error.message` — see player-error.js. It printed a request id,
         a server file path and a line number at the player, and in production
         it degrades to a bare "Server Error" instead. */
      say(playerFacingError(error,
        uiText('collection.exchange.purchaseFailed', 'Purchase failed.')), 'error');
    }
    render();
  }

  /** The one primary control, chosen by viewer state and listing state. */
  function renderPrimary() {
    if (state.phase === 'done') {
      const doneEl = document.createElement('p');
      doneEl.className = 'exchange-detail-done';
      doneEl.textContent = state.doneMessage;
      actions.appendChild(doneEl);
      return;
    }

    const { viewer } = state;
    const price = Number(state.listing.unitPrice) || 0;

    if (viewer.state === 'resolving') {
      /*
       * A BLOCK, NOT A LABEL — no wrong words are ever shown.
       *
       * It holds the box the arriving button will occupy, and it does that by
       * CARRYING THAT BUTTON'S OWN LABEL, hidden. A fixed measure cannot do
       * this: "Buy - 100 Stardust" is 189px in English and longer in German,
       * and an earlier version that let the shared row rule collapse it to its
       * padding rendered a 36px CIRCLE that snapped 153px wider when the real
       * button landed. Sizing from the text keeps it right in all 8 languages
       * and at any price. The owner's "Cancel listing" is narrower, so their
       * row settles slightly inward rather than jumping outward.
       */
      const pending = document.createElement('div');
      pending.className = 'starter-reward-cta exchange-detail-pending';
      pending.setAttribute('aria-label', uiText('exchange.detail.checking', 'Checking your account'));
      pending.setAttribute('role', 'status');
      const ghost = document.createElement('span');
      ghost.className = 'exchange-detail-pending-measure';
      ghost.setAttribute('aria-hidden', 'true');
      ghost.textContent = buyLabel(price);
      pending.appendChild(ghost);
      actions.appendChild(pending);
      return;
    }

    if (!state.listing.isAvailable) {
      /* Nothing to buy and nothing to cancel. The only honest action left is a
         way onward, and only where there IS one: on the link door the reader is
         standing on `/play` with no exchange open, but a reader who clicked a
         row is already looking at it and a button that closes the dialog they
         are in is the "makes no sense" button this screen was rebuilt to
         remove. */
      if (viewer.state === 'guest') {
        const signup = document.createElement('button');
        signup.type = 'button';
        signup.className = 'starter-reward-cta exchange-detail-signup';
        signup.textContent = uiText('exchange.detail.signUpFree', 'Create a free account');
        signup.addEventListener('click', () => onSignUp?.(state.listing));
        actions.appendChild(signup);
      } else if (!exchangeIsOnScreen()) {
        const browse = document.createElement('button');
        browse.type = 'button';
        browse.className = 'starter-reward-cta exchange-detail-browse';
        browse.textContent = uiText('exchange.detail.browse', 'Browse the Moon Exchange');
        browse.addEventListener('click', () => onBrowse?.(state.listing));
        actions.appendChild(browse);
      }
      return;
    }

    if (viewer.state === 'guest') {
      const signup = document.createElement('button');
      signup.type = 'button';
      signup.className = 'starter-reward-cta exchange-detail-signup';
      signup.textContent = uiText('exchange.detail.signUp', 'Create an account to buy');
      signup.addEventListener('click', () => onSignUp?.(state.listing));
      actions.appendChild(signup);
      return;
    }

    if (!viewer.actionable) {
      /* We could not establish whether this listing is theirs, so neither Buy
         nor Cancel can be offered honestly. The Exchange can answer it. */
      if (!exchangeIsOnScreen()) {
        const browse = document.createElement('button');
        browse.type = 'button';
        browse.className = 'starter-reward-cta exchange-detail-browse';
        browse.textContent = uiText('exchange.detail.browse', 'Browse the Moon Exchange');
        browse.addEventListener('click', () => onBrowse?.(state.listing));
        actions.appendChild(browse);
      }
      return;
    }

    if (viewer.isMine) {
      const cancelBtn = document.createElement('button');
      cancelBtn.type = 'button';
      cancelBtn.className = 'starter-reward-cta exchange-detail-cancel';
      cancelBtn.textContent = uiText('collection.exchange.cancelListing', 'Cancel listing');
      cancelBtn.disabled = state.phase === 'busy' || typeof cancel !== 'function';
      cancelBtn.addEventListener('click', () => run('cancel', () => cancel(state.listing)));
      actions.appendChild(cancelBtn);
      return;
    }

    /* CLIENT MIRRORS SERVER, on BOTH refusals it can see coming. The purchase
       is refused server-side when the balance is short and when the account has
       not unlocked the Exchange; refusing both here means the reader is never
       invited to press something that cannot work. The lock was missing, and a
       locked reader who pressed Buy got a stack frame back. */
    const balance = state.viewer.stardustBalance;
    const short = Number.isFinite(balance) && balance < price;
    const locked = Boolean(state.viewer.exchangeUnlock);
    const buyBtn = document.createElement('button');
    buyBtn.type = 'button';
    buyBtn.className = 'starter-reward-cta exchange-detail-buy';
    buyBtn.textContent = buyLabel(price);
    buyBtn.disabled = short || locked || state.phase === 'busy' || typeof buy !== 'function';
    buyBtn.addEventListener('click', () => run('buy', () => buy(state.listing, 1)));
    actions.appendChild(buyBtn);
  }

  function render() {
    const { viewer } = state;
    const gone = !state.listing.isAvailable;

    if (gone && state.phase !== 'done') {
      eyebrow.textContent = uiText('exchange.detail.gone', 'This one is no longer available');
    } else if (state.phase === 'done') {
      /* Name the OUTCOME. "Done" over a card you just bought is the flattest
         possible reading of the one moment on this screen worth celebrating. */
      eyebrow.textContent = state.doneKind === 'cancel'
        ? uiText('exchange.detail.withdrawn', 'Withdrawn')
        : uiText('exchange.detail.claimed', 'Claimed');
    } else if (viewer.state === 'player' && viewer.isMine) {
      eyebrow.textContent = state.listing.isStellar
        ? uiText('exchange.detail.mineStellar', 'Your stellar listing')
        : uiText('exchange.detail.mine', 'Your listing');
    } else {
      eyebrow.textContent = state.listing.isStellar
        ? uiText('exchange.detail.setAsideStellar', 'A stellar card is set aside for you')
        : uiText('exchange.detail.setAside', 'A card is set aside for you');
    }

    actions.textContent = '';
    renderPrimary();

    /* Share is what you do AFTER deciding: a quiet text link, never a second
       button competing with the primary. Not offered for a listing that is
       gone — a link nobody can act on is not worth sending. */
    if (!gone && state.phase !== 'done') {
      const share = document.createElement('button');
      share.type = 'button';
      share.className = 'exchange-detail-share';
      share.textContent = uiText('exchange.share.button', 'Share listing');
      share.addEventListener('click', () => openListingShare(state.listing));
      actions.appendChild(share);
    }

    /* THE PRICE IS ALWAYS ON SCREEN. It rides on the button when the button
       spends it; when it cannot — a visitor, your own listing, sold out — it is
       stated here instead, or a reader is asked to make an account without ever
       being told what the card costs. */
    const price = Number(state.listing.unitPrice) || 0;
    const priceOnButton = viewer.state === 'player' && viewer.actionable && !viewer.isMine
      && !gone && state.phase === 'idle';
    const parts = [];
    /* NOTHING STALE UNDER A CONFIRMATION. After a purchase the stock and the
       balance on this line are both one transaction out of date — measured:
       "Claimed" sat above "2 left · You have 2,000 Stardust" on a card that had
       just cost 100 and left one behind. The outcome message is the whole
       content of that state. */
    if (!gone && state.phase !== 'done') {
      if (!priceOnButton) {
        parts.push(uiText('exchange.detail.price', '{{price}} Stardust', { price }));
      }
      parts.push(uiText('exchange.share.remaining', '{{count}} left',
        { count: state.listing.quantityRemaining }));
      /* The balance belongs to a DECISION about spending. On your own listing
         there is nothing to spend, so it is noise beside a Cancel button; on a
         listing that is gone the whole line is, including the price, which
         would otherwise read as an offer. */
      if (viewer.state === 'player' && viewer.actionable && !viewer.isMine
          && Number.isFinite(viewer.stardustBalance)) {
        parts.push(uiText('exchange.detail.balance', 'You have {{amount}} Stardust',
          { amount: Number(viewer.stardustBalance).toLocaleString() }));
      }
      /* AND WHY THE BUTTON IS DEAD. A disabled control with no reason beside it
         reads as a broken screen; this is the same sentence the Exchange grid
         shows, from the same helper, so the two doors cannot word it
         differently. */
      if (viewer.exchangeUnlock) parts.push(exchangeLockMessage(viewer.exchangeUnlock));
    }
    facts.textContent = parts.join(' · ');
    facts.hidden = parts.length === 0;

    /* Said once, next to the price it is measured against — not as a separate
       error the reader has to connect back to a disabled button. */
    if (viewer.state === 'player' && !viewer.actionable) {
      say(uiText('exchange.detail.unreadable', 'Could not check this listing against your account.'), 'error');
    } else if (!gone && state.phase === 'idle' && viewer.state === 'player' && !viewer.isMine
        && Number.isFinite(viewer.stardustBalance)
        && viewer.stardustBalance < price) {
      say(uiText('exchange.detail.short', 'Not enough Stardust for this card.'), 'error');
    } else if (state.phase === 'idle' && status.dataset.tone !== 'error') {
      say('');
    }
  }

  render();

  el.appendChild(panel);
  document.body.appendChild(el);

  const onKey = (event) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      closeListingDetail('user');
    }
  };
  document.addEventListener('keydown', onKey, true);
  open = { el, onKey, render, onClose, state, door, releaseHover, card: options.card || null };
  /* Focus the DIALOG, not the close button. Focusing the X put a visible ring
     on it the moment the surface opened, which reads as a rendering fault and
     points the eye at the way out rather than at the card. The panel takes
     focus for screen readers and Escape, and `:focus-visible` still rings the X
     for anyone who tabs to it. */
  panel.setAttribute('tabindex', '-1');
  panel.focus({ preventScroll: true });
  return el;
}

export const MoonfallExchangeDetail = {
  openListingDetail,
  closeListingDetail,
  updateListingDetail,
  currentListingId,
  normalizeListing,
};
